// dForms / Templates
//
// Lists the form templates people can pick in dForms and lets an admin add,
// edit, reorder, enable and disable them. Templates are built as normal
// forms in dForms and copied from there - there is no form editor here.
//
// This page only talks to dAdmin's own backend (/api/dforms-templates),
// which checks dForms > Templates access and forwards to the dForms admin
// API. dForms error messages are shown to the user unchanged.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    FaPlus, FaSearch, FaEllipsisV, FaTimes, FaWpforms, FaEye, FaPen, FaSyncAlt,
    FaImage, FaRedo, FaExclamationTriangle, FaCheck,
    FaTrashAlt, FaExternalLinkAlt, FaClock, FaGripVertical, FaSave,
} from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { apiFetch } from "./utils/api";
import { dnAlert } from "./utils/dNews_Alert";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./dForms_Templates.css";

const API = "/api/dforms-templates";

const TITLE_MAX = 80;
const DESCRIPTION_MAX = 300;
const CATEGORY_MAX = 40;
const THUMBNAIL_MAX = 2 * 1024 * 1024;
const THUMBNAIL_EXTS = [".png", ".jpg", ".jpeg", ".webp"];
const THUMBNAIL_TYPES = ["image/png", "image/jpeg", "image/webp"];
const SEARCH_DEBOUNCE_MS = 300;

const NOT_CONFIGURED = "Template management is not configured";
const ADDED_MESSAGE = "Template added. It is switched off until you check it and switch it on.";
const EMPTY_MESSAGE = "No templates yet. Build a form in dForms, offer it as a template, then add it here.";
const SEARCH_NOTE = "Only forms their owners offered as templates, and your own forms, are listed.";

// ── API helper ────────────────────────────────────────────────────────────
// Always resolves: { ok, status, data, error, notConfigured }.
async function callApi(path, options = {}) {
    let res;
    try {
        res = await apiFetch(`${API}${path}`, options);
    } catch {
        return { ok: false, status: 0, data: null, error: "Could not reach dAdmin. Please check your connection and try again." };
    }
    let data = null;
    try { data = await res.json(); } catch { /* empty or not JSON */ }
    const error = res.ok ? "" : ((data && (data.error || data.message)) || `Request failed (${res.status}).`);
    return {
        ok: res.ok,
        status: res.status,
        data,
        error,
        notConfigured: res.status === 503 && !!(data && data.notConfigured),
    };
}

const jsonOptions = (method, body) => ({
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
});

// ── Formatting ────────────────────────────────────────────────────────────
const plural = (n, one, many) => `${Number(n) || 0} ${Number(n) === 1 ? one : many}`;
const countsLabel = (t) => `${plural(t.page_count, "page", "pages")}, ${plural(t.question_count, "question", "questions")}`;

function formatDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// dForms sends source_status: "none" (built in), "deleted", "not_offered"
// (owner switched off "Offer this form as a template") or "ok". Older
// answers without it are worked out from the id and title.
function sourceStatus(t) {
    if (t.source_status) return t.source_status;
    if (t.source_form_id == null) return "none";
    if (!t.source_form_title) return "deleted";
    return "ok";
}

function sourceLabel(t) {
    switch (sourceStatus(t)) {
        case "none":        return { text: "Built in", kind: "builtin" };
        case "deleted":     return { text: "Source form deleted", kind: "deleted" };
        case "not_offered": return { text: `${t.source_form_title || "Source form"} (no longer offered)`, kind: "not_offered" };
        default:            return { text: t.source_form_title || "Source form", kind: "ok" };
    }
}

// Why Refresh from source cannot run for this template, or "" when it can.
function refreshUnavailableReason(t) {
    switch (sourceStatus(t)) {
        case "ok":          return "";
        case "none":        return "Built-in template: no source form";
        case "deleted":     return "Source form was deleted";
        case "not_offered": return "The owner no longer offers this form as a template";
        default:            return "The source form cannot be used right now";
    }
}

function thumbnailUrl(publicBase, t) {
    if (!publicBase || !t.thumbnail_path) return "";
    const p = String(t.thumbnail_path).replace(/^\/+/, "");
    return `${publicBase}/${p}${t.updated_at ? `?v=${encodeURIComponent(t.updated_at)}` : ""}`;
}

// Same order dForms shows: sort_order, then title.
function byOrder(a, b) {
    const d = (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
    if (d !== 0) return d;
    return String(a.title || "").localeCompare(String(b.title || ""), undefined, { sensitivity: "base" });
}

// Details shown in the preview header: [label, value] pairs.
function templateDetails(t) {
    const updated = formatDate(t.updated_at);
    return [
        ["Category", t.category || "None"],
        ["Content", countsLabel(t)],
        ["Source form", sourceLabel(t).text],
        ["Times used", String(Number(t.times_used) || 0)],
        ["Last updated", updated ? `${updated}${t.updated_by_name ? ` by ${t.updated_by_name}` : ""}` : "Unknown"],
    ];
}

function formDetails(f) {
    return [
        ["Owner", f.is_own ? "You" : (f.owner_name || f.owner_emp_id || "Unknown")],
        ["Content", countsLabel(f)],
        ["Last updated", formatDate(f.updated_at) || "Unknown"],
    ];
}

function validateDetails({ title, description, category }) {
    const t = title.trim();
    if (!t) return "Title is required.";
    if (t.length > TITLE_MAX) return `Title must be ${TITLE_MAX} characters or fewer.`;
    if (description.trim().length > DESCRIPTION_MAX) return `Description must be ${DESCRIPTION_MAX} characters or fewer.`;
    if (category.trim().length > CATEGORY_MAX) return `Category must be ${CATEGORY_MAX} characters or fewer.`;
    return "";
}

// ── Page ──────────────────────────────────────────────────────────────────
export default function DFormsTemplates({ navSize }) {
    const [publicBase, setPublicBase] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [notConfigured, setNotConfigured] = useState(false);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("");

    const [busyId, setBusyId] = useState(null);     // row with a switch / move in flight
    const [menu, setMenu] = useState(null);         // { id, rect, trigger }
    const [viewTpl, setViewTpl] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [editTpl, setEditTpl] = useState(null);
    const [refreshTpl, setRefreshTpl] = useState(null);
    const [thumbTpl, setThumbTpl] = useState(null);
    const [orderDraft, setOrderDraft] = useState(null);  // non-null = reorder mode
    const [savingOrder, setSavingOrder] = useState(false);

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) { setLoading(true); setLoadError(""); }
        const cfg = await callApi("/config");
        if (!cfg.ok) {
            setLoading(false);
            if (!silent) setLoadError(cfg.error);
            return;
        }
        if (!cfg.data || !cfg.data.configured) {
            setNotConfigured(true);
            setLoading(false);
            return;
        }
        setPublicBase(cfg.data.publicBase);
        const r = await callApi("/templates");
        setLoading(false);
        if (r.notConfigured) { setNotConfigured(true); return; }
        if (!r.ok) {
            if (silent) dnAlert.error("Could not refresh the list", r.error);
            else setLoadError(r.error);
            return;
        }
        setNotConfigured(false);
        setLoadError("");
        setTemplates(Array.isArray(r.data) ? r.data : []);
    }, []);

    useEffect(() => { load(); }, [load]);

    const ordered = useMemo(() => [...templates].sort(byOrder), [templates]);

    const categories = useMemo(() => {
        const seen = new Map();
        for (const t of templates) {
            const c = String(t.category || "").trim();
            if (c && !seen.has(c.toLowerCase())) seen.set(c.toLowerCase(), c);
        }
        return [...seen.values()].sort((a, b) => a.localeCompare(b));
    }, [templates]);

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return ordered.filter(t => {
            if (q && !String(t.title || "").toLowerCase().includes(q)) return false;
            if (statusFilter === "enabled" && !t.enabled) return false;
            if (statusFilter === "disabled" && t.enabled) return false;
            if (categoryFilter && String(t.category || "").trim().toLowerCase() !== categoryFilter.toLowerCase()) return false;
            return true;
        });
    }, [ordered, search, statusFilter, categoryFilter]);

    const filtersActive = !!(search.trim() || statusFilter !== "all" || categoryFilter);
    const clearFilters = () => { setSearch(""); setStatusFilter("all"); setCategoryFilter(""); };

    // ── Enable / disable ──
    const toggleEnabled = async (t) => {
        const next = !t.enabled;
        const ok = await dnAlert.confirm(next
            ? {
                title: "Switch on template",
                message: `Show ${t.title} in dForms? People can start a new form from it.`,
                confirmLabel: "Switch on",
            }
            : {
                title: "Switch off template",
                message: `Hide ${t.title} from dForms? People can no longer start a form from it. Forms already made from it are not affected.`,
                confirmLabel: "Switch off",
                danger: true,
            });
        if (!ok) return;
        setBusyId(t.id);
        const r = await callApi(`/templates/${encodeURIComponent(t.id)}`, jsonOptions("PUT", { enabled: next }));
        setBusyId(null);
        if (r.notConfigured) { setNotConfigured(true); return; }
        if (!r.ok) { dnAlert.error(next ? "Could not switch on" : "Could not switch off", r.error); return; }
        dnAlert.success(next ? "Template switched on" : "Template switched off", t.title);
        load({ silent: true });
    };

    // ── Reorder mode: drag the six-dot handles, then Save order ──
    // The whole list is shown (filters do not apply) in the order dForms uses.
    const reordering = orderDraft !== null;
    const orderChanged = reordering && orderDraft.some((t, i) => !ordered[i] || ordered[i].id !== t.id);

    const startReorder = () => {
        setMenu(null);
        setOrderDraft([...ordered]);
    };

    const cancelReorder = async () => {
        if (savingOrder) return;
        if (orderChanged) {
            const ok = await dnAlert.confirm({
                title: "Discard the new order?",
                message: "The order you set has not been saved. Leave reorder mode without saving?",
                confirmLabel: "Discard",
                cancelLabel: "Keep reordering",
                danger: true,
            });
            if (!ok) return;
        }
        setOrderDraft(null);
    };

    const onDragEnd = (result) => {
        if (!result.destination || result.destination.index === result.source.index) return;
        setOrderDraft(list => {
            const items = [...list];
            const [moved] = items.splice(result.source.index, 1);
            items.splice(result.destination.index, 0, moved);
            return items;
        });
    };

    // Number the list 1..n and save only the rows whose sort_order changes.
    // If one save fails, the ones already saved are put back.
    const saveOrder = async () => {
        if (!orderChanged || savingOrder) return;
        const updates = orderDraft
            .map((t, i) => ({ id: t.id, sort_order: i + 1, previous: Number(t.sort_order) || 0 }))
            .filter(u => u.sort_order !== u.previous);
        setSavingOrder(true);
        const done = [];
        for (const u of updates) {
            const r = await callApi(`/templates/${encodeURIComponent(u.id)}`, jsonOptions("PUT", { sort_order: u.sort_order }));
            if (!r.ok) {
                for (const d of done) {
                    await callApi(`/templates/${encodeURIComponent(d.id)}`, jsonOptions("PUT", { sort_order: d.previous }));
                }
                setSavingOrder(false);
                if (r.notConfigured) { setOrderDraft(null); setNotConfigured(true); return; }
                dnAlert.error("Could not save the order", r.error);
                load({ silent: true });
                return;
            }
            done.push(u);
        }
        setSavingOrder(false);
        setOrderDraft(null);
        dnAlert.success("Order saved", "dForms now shows the templates in this order.");
        load({ silent: true });
    };

    // ── Remove template (row menu; only when switched off) ──
    // dForms deletes only the template; the source form and forms already made
    // from it are not touched. 409 if it is still switched on.
    const removeTemplate = async (t) => {
        const ok = await dnAlert.confirm({
            title: "Remove template",
            message: `Remove ${t.title}? It is deleted from the template list for good. The form it came from and forms people already made from it are not affected.`,
            confirmLabel: "Remove template",
            cancelLabel: "Cancel",
            danger: true,
        });
        if (!ok) return;
        setBusyId(t.id);
        const r = await callApi(`/templates/${encodeURIComponent(t.id)}`, { method: "DELETE" });
        setBusyId(null);
        if (r.notConfigured) { setNotConfigured(true); return; }
        if (!r.ok) { dnAlert.error("Could not remove the template", r.error); return; }
        setTemplates(list => list.filter(x => x.id !== t.id));
        dnAlert.success("Template removed", t.title);
        // The row and its menu button are gone; put focus somewhere sensible.
        const search = document.getElementById("dft-search");
        if (search) search.focus();
        load({ silent: true });
    };

    // ── Remove thumbnail (from the thumbnail dialog). Resolves true on success. ──
    const removeThumbnail = async (t) => {
        const ok = await dnAlert.confirm({
            title: "Remove thumbnail",
            message: `Remove the thumbnail of ${t.title}? dForms will show its plain card instead.`,
            confirmLabel: "Remove thumbnail",
            danger: true,
        });
        if (!ok) return false;
        setBusyId(t.id);
        const r = await callApi(`/templates/${encodeURIComponent(t.id)}/thumbnail`, { method: "DELETE" });
        setBusyId(null);
        if (r.notConfigured) { setNotConfigured(true); return false; }
        if (!r.ok) { dnAlert.error("Could not remove the thumbnail", r.error); return false; }
        const updated = r.data && r.data.id != null ? r.data : { ...t, thumbnail_path: null };
        setTemplates(list => list.map(x => (x.id === t.id ? { ...x, ...updated } : x)));
        dnAlert.success("Thumbnail removed", t.title);
        load({ silent: true });
        return true;
    };

    const openMenu = (t, e) => {
        const trigger = e.currentTarget;
        if (menu && menu.id === t.id) { setMenu(null); return; }
        setMenu({ id: t.id, rect: trigger.getBoundingClientRect(), trigger });
    };

    const menuTemplate = menu ? ordered.find(x => x.id === menu.id) : null;

    // ── Render ──
    let body;
    if (notConfigured) {
        body = (
            <div className="dft-state dft-state-warn" role="status">
                <FaExclamationTriangle className="dft-state-icon" aria-hidden="true" />
                <h3>{NOT_CONFIGURED}</h3>
                <p>The connection to dForms is not set up on the dAdmin server yet. Please contact the dAdmin administrator.</p>
                <button type="button" className="dft-btn dft-btn-secondary" onClick={() => load()}>
                    <FaRedo aria-hidden="true" /> Retry
                </button>
            </div>
        );
    } else if (loading) {
        body = <SkeletonTable />;
    } else if (loadError) {
        body = (
            <div className="dft-state dft-state-error" role="alert">
                <FaExclamationTriangle className="dft-state-icon" aria-hidden="true" />
                <h3>Could not load templates</h3>
                <p>{loadError}</p>
                <button type="button" className="dft-btn dft-btn-secondary" onClick={() => load()}>
                    <FaRedo aria-hidden="true" /> Retry
                </button>
            </div>
        );
    } else if (reordering) {
        body = (
            <ReorderList
                items={orderDraft}
                publicBase={publicBase}
                disabled={savingOrder}
                onDragEnd={onDragEnd}
            />
        );
    } else if (templates.length === 0) {
        body = (
            <div className="dft-state">
                <FaWpforms className="dft-state-icon" aria-hidden="true" />
                <p>{EMPTY_MESSAGE}</p>
            </div>
        );
    } else if (visible.length === 0) {
        body = (
            <div className="dft-state">
                <FaSearch className="dft-state-icon" aria-hidden="true" />
                <p>No templates match these filters.</p>
                <button type="button" className="dft-btn dft-btn-secondary" onClick={clearFilters}>Clear filters</button>
            </div>
        );
    } else {
        body = (
            <div className="dft-table-wrap">
                <table className="dft-table">
                    <caption className="dft-sr-only">Form templates, in the order dForms shows them</caption>
                    <thead>
                        <tr>
                            <th scope="col">Thumbnail</th>
                            <th scope="col">Template</th>
                            <th scope="col">Category</th>
                            <th scope="col">Content</th>
                            <th scope="col">Times used</th>
                            <th scope="col">Source form</th>
                            <th scope="col">Last updated</th>
                            <th scope="col">Enabled</th>
                            <th scope="col"><span className="dft-sr-only">Actions</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map(t => {
                            const src = sourceLabel(t);
                            const thumb = thumbnailUrl(publicBase, t);
                            const busy = busyId === t.id;
                            const switchId = `dft-switch-${t.id}`;
                            return (
                                <tr key={t.id} className={t.enabled ? "" : "dft-row-off"}>
                                    <td className="dft-cell-thumb">
                                        {thumb
                                            ? <img className="dft-thumb" src={thumb} alt={`Thumbnail of ${t.title}`} loading="lazy" />
                                            : <div className="dft-thumb dft-thumb-empty" role="img" aria-label="No thumbnail"><FaWpforms aria-hidden="true" /></div>}
                                    </td>
                                    <td className="dft-cell-title">
                                        <div className="dft-title">{t.title}</div>
                                        {t.description && <div className="dft-desc">{t.description}</div>}
                                    </td>
                                    <td data-label="Category">
                                        {t.category ? <span className="dft-chip">{t.category}</span> : <span className="dft-muted">None</span>}
                                    </td>
                                    <td data-label="Content">{countsLabel(t)}</td>
                                    <td data-label="Times used">{Number(t.times_used) || 0}</td>
                                    <td data-label="Source form">
                                        <span className={`dft-source dft-source-${src.kind}`}>
                                            {(src.kind === "deleted" || src.kind === "not_offered") && <FaExclamationTriangle aria-hidden="true" />}
                                            {src.text}
                                        </span>
                                    </td>
                                    <td data-label="Last updated">
                                        <div>{formatDate(t.updated_at) || <span className="dft-muted">Unknown</span>}</div>
                                        {t.updated_by_name && <div className="dft-muted dft-small">by {t.updated_by_name}</div>}
                                    </td>
                                    <td data-label="Enabled">
                                        <label className="dft-switch-wrap" htmlFor={switchId}>
                                            <span className="dft-switch">
                                                <input
                                                    id={switchId}
                                                    type="checkbox"
                                                    role="switch"
                                                    checked={!!t.enabled}
                                                    aria-checked={!!t.enabled}
                                                    disabled={busy}
                                                    onChange={() => toggleEnabled(t)}
                                                />
                                                <span className="dft-slider" aria-hidden="true" />
                                            </span>
                                            <span className="dft-switch-text">{t.enabled ? "On" : "Off"}</span>
                                            <span className="dft-sr-only">Enabled: {t.title}</span>
                                        </label>
                                    </td>
                                    <td className="dft-cell-menu">
                                        <button
                                            type="button"
                                            className="dft-icon-btn"
                                            aria-label={`Actions for ${t.title}`}
                                            aria-haspopup="menu"
                                            aria-expanded={menu && menu.id === t.id ? "true" : "false"}
                                            disabled={busy}
                                            onClick={(e) => openMenu(t, e)}
                                        >
                                            <FaEllipsisV aria-hidden="true" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="dspr-container">
            <div className={`dspr-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <span className="navbar-brand"><img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} /></span>
                        Form templates
                    </h2>
                    <p className="dspr-subtitle">Templates people can start a new form from in dForms. Build the form in dForms, offer it as a template from its Settings, then add it here.</p>
                </div>

                <div className="dspr-dashboard-content">
                    <div className="dft-card">
                        {!notConfigured && reordering && (
                            <div className="dft-reorder-bar">
                                <div className="dft-reorder-help">
                                    <strong><FaGripVertical aria-hidden="true" /> Reorder templates</strong>
                                    <span id="dft-reorder-instructions">
                                        Drag a template by its six-dot handle to set the order dForms shows. With a keyboard: Tab to a handle, press Space, move with the arrow keys, then press Space again.
                                    </span>
                                </div>
                                <div className="dft-reorder-actions">
                                    <button type="button" className="dft-btn dft-btn-secondary" onClick={cancelReorder} disabled={savingOrder}>Cancel</button>
                                    <button type="button" className="dft-btn dft-btn-primary" onClick={saveOrder} disabled={!orderChanged || savingOrder}>
                                        <FaSave aria-hidden="true" /> {savingOrder ? "Saving order..." : "Save order"}
                                    </button>
                                </div>
                            </div>
                        )}
                        {!notConfigured && !reordering && (
                            <div className="dft-toolbar">
                                <div className="dft-search">
                                    <label htmlFor="dft-search" className="dft-sr-only">Search templates by title</label>
                                    <FaSearch className="dft-search-icon" aria-hidden="true" />
                                    <input
                                        id="dft-search"
                                        type="search"
                                        className="dft-input"
                                        placeholder="Search by title"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                                <div className="dft-filter">
                                    <label htmlFor="dft-status" className="dft-filter-label">Status</label>
                                    <select id="dft-status" className="dft-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                        <option value="all">All</option>
                                        <option value="enabled">Enabled</option>
                                        <option value="disabled">Disabled</option>
                                    </select>
                                </div>
                                <div className="dft-filter">
                                    <label htmlFor="dft-category" className="dft-filter-label">Category</label>
                                    <select id="dft-category" className="dft-input" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                                        <option value="">All categories</option>
                                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <button
                                    type="button"
                                    className="dft-btn dft-btn-secondary dft-add-btn"
                                    onClick={startReorder}
                                    disabled={loading || !!loadError || templates.length < 2}
                                    title={templates.length < 2 ? "Add at least two templates to reorder them" : undefined}
                                >
                                    <FaGripVertical aria-hidden="true" /> Reorder
                                </button>
                                <button type="button" className="dft-btn dft-btn-primary" onClick={() => setAddOpen(true)} disabled={loading || !!loadError}>
                                    <FaPlus aria-hidden="true" /> Add template
                                </button>
                            </div>
                        )}
                        {!notConfigured && !loading && !loadError && templates.length > 0 && !reordering && (
                            <p className="dft-count" aria-live="polite">
                                Showing {visible.length} of {plural(templates.length, "template", "templates")}
                                {filtersActive ? " (filtered)" : ""}
                            </p>
                        )}
                        {body}
                    </div>
                </div>
            </div>

            {menu && menuTemplate && (
                <RowMenu
                    rect={menu.rect}
                    trigger={menu.trigger}
                    onClose={() => setMenu(null)}
                    items={[
                        { label: "View", icon: FaEye, onSelect: () => setViewTpl(menuTemplate) },
                        { label: "Edit details", icon: FaPen, onSelect: () => setEditTpl(menuTemplate) },
                        {
                            label: "Refresh from source",
                            icon: FaSyncAlt,
                            unavailableReason: refreshUnavailableReason(menuTemplate),
                            onSelect: () => setRefreshTpl(menuTemplate),
                        },
                        { label: "Change thumbnail", icon: FaImage, onSelect: () => setThumbTpl(menuTemplate) },
                        {
                            label: "Remove template",
                            icon: FaTrashAlt,
                            danger: true,
                            unavailableReason: menuTemplate.enabled ? "Switch it off first" : "",
                            onSelect: () => removeTemplate(menuTemplate),
                        },
                    ]}
                />
            )}

            {viewTpl && (
                <PreviewDialog
                    request={{ template_id: viewTpl.id }}
                    name={viewTpl.title}
                    badge={viewTpl.enabled
                        ? { text: "On", kind: "on", hint: "Shown in dForms" }
                        : { text: "Off", kind: "off", hint: "Hidden from dForms" }}
                    details={templateDetails(viewTpl)}
                    onClose={() => setViewTpl(null)}
                />
            )}
            {addOpen && (
                <AddDialog
                    categories={categories}
                    onClose={() => setAddOpen(false)}
                    onAdded={() => { setAddOpen(false); load({ silent: true }); }}
                    onNotConfigured={() => { setAddOpen(false); setNotConfigured(true); }}
                />
            )}
            {editTpl && (
                <EditDialog
                    template={editTpl}
                    categories={categories}
                    onClose={() => setEditTpl(null)}
                    onSaved={() => { setEditTpl(null); load({ silent: true }); }}
                    onNotConfigured={() => { setEditTpl(null); setNotConfigured(true); }}
                />
            )}
            {refreshTpl && (
                <RefreshDialog
                    template={refreshTpl}
                    onClose={() => setRefreshTpl(null)}
                    onDone={(updated) => {
                        if (updated && updated.id != null) {
                            setTemplates(list => list.map(x => (x.id === updated.id ? { ...x, ...updated } : x)));
                        }
                        setRefreshTpl(null);
                        load({ silent: true });
                    }}
                    onNotConfigured={() => { setRefreshTpl(null); setNotConfigured(true); }}
                />
            )}
            {thumbTpl && (
                <ThumbnailDialog
                    template={thumbTpl}
                    currentUrl={thumbnailUrl(publicBase, thumbTpl)}
                    onClose={() => setThumbTpl(null)}
                    onDone={() => { setThumbTpl(null); load({ silent: true }); }}
                    onRemove={async () => { if (await removeThumbnail(thumbTpl)) setThumbTpl(null); }}
                    onNotConfigured={() => { setThumbTpl(null); setNotConfigured(true); }}
                />
            )}
        </div>
    );
}

// ── Reorder list (drag handles; keyboard dragging is built in) ──────────
function ReorderList({ items, publicBase, disabled, onDragEnd }) {
    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="dft-reorder">
                {(provided) => (
                    <div
                        className="dft-reorder-list"
                        role="list"
                        aria-label="Templates in the order dForms shows them"
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                    >
                        {items.map((t, i) => {
                            const thumb = thumbnailUrl(publicBase, t);
                            return (
                                <Draggable key={t.id} draggableId={String(t.id)} index={i} isDragDisabled={disabled}>
                                    {(prov, snap) => (
                                        <div
                                            className={`dft-reorder-item ${snap.isDragging ? "dft-reorder-dragging" : ""}`}
                                            role="listitem"
                                            ref={prov.innerRef}
                                            {...prov.draggableProps}
                                        >
                                            <span
                                                className="dft-drag"
                                                title="Drag to reorder"
                                                {...prov.dragHandleProps}
                                                aria-label={`Reorder ${t.title}, position ${i + 1} of ${items.length}`}
                                                aria-describedby="dft-reorder-instructions"
                                            >
                                                <FaGripVertical aria-hidden="true" />
                                            </span>
                                            <span className="dft-reorder-pos" aria-hidden="true">{i + 1}</span>
                                            {thumb
                                                ? <img className="dft-thumb dft-thumb-sm" src={thumb} alt={`Thumbnail of ${t.title}`} />
                                                : <span className="dft-thumb dft-thumb-sm dft-thumb-empty" role="img" aria-label="No thumbnail"><FaWpforms aria-hidden="true" /></span>}
                                            <span className="dft-reorder-text">
                                                <span className="dft-title">{t.title}</span>
                                                <span className="dft-muted dft-small">{countsLabel(t)}{t.category ? ` | ${t.category}` : ""}</span>
                                            </span>
                                            <span className={`dft-badge dft-badge-${t.enabled ? "on" : "off"}`}>{t.enabled ? "On" : "Off"}</span>
                                        </div>
                                    )}
                                </Draggable>
                            );
                        })}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
}

// ── Loading skeleton ──────────────────────────────────────────────────────
function SkeletonTable() {
    return (
        <div className="dft-skeleton" aria-busy="true" aria-live="polite">
            <span className="dft-sr-only">Loading templates</span>
            {[0, 1, 2, 3, 4].map(i => (
                <div className="dft-skeleton-row" key={i} aria-hidden="true">
                    <span className="dft-sk dft-sk-thumb" />
                    <span className="dft-sk-lines">
                        <span className="dft-sk dft-sk-line" style={{ width: "45%" }} />
                        <span className="dft-sk dft-sk-line" style={{ width: "70%" }} />
                    </span>
                    <span className="dft-sk dft-sk-pill" />
                    <span className="dft-sk dft-sk-pill" />
                    <span className="dft-sk dft-sk-switch" />
                </div>
            ))}
        </div>
    );
}

// ── Row menu (keyboard usable, positioned next to its trigger) ────────────
function RowMenu({ rect, trigger, items, onClose }) {
    const menuRef = useRef(null);
    const reasonBase = useRef(`dft-menu-reason-${Math.random().toString(36).slice(2)}`).current;

    // Unavailable items stay focusable (aria-disabled) so their reason can be read.
    const focusables = () => Array.from(menuRef.current ? menuRef.current.querySelectorAll("button") : []);

    useEffect(() => {
        const first = focusables()[0];
        if (first) first.focus();
        const onDown = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target) && !trigger.contains(e.target)) onClose();
        };
        const onScroll = () => onClose();
        document.addEventListener("mousedown", onDown);
        window.addEventListener("resize", onScroll);
        window.addEventListener("scroll", onScroll, true);
        return () => {
            document.removeEventListener("mousedown", onDown);
            window.removeEventListener("resize", onScroll);
            window.removeEventListener("scroll", onScroll, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onKeyDown = (e) => {
        const list = focusables();
        const i = list.indexOf(document.activeElement);
        if (e.key === "ArrowDown") { e.preventDefault(); list[(i + 1) % list.length]?.focus(); }
        else if (e.key === "ArrowUp") { e.preventDefault(); list[(i - 1 + list.length) % list.length]?.focus(); }
        else if (e.key === "Home") { e.preventDefault(); list[0]?.focus(); }
        else if (e.key === "End") { e.preventDefault(); list[list.length - 1]?.focus(); }
        else if (e.key === "Escape") { e.preventDefault(); onClose(); trigger.focus(); }
        else if (e.key === "Tab") { onClose(); }
    };

    const MENU_W = 240;
    const MENU_H = items.length * 40 + items.filter(it => it.unavailableReason).length * 20 + 12;
    const left = Math.max(8, Math.min(rect.right - MENU_W, window.innerWidth - MENU_W - 8));
    const openUp = rect.bottom + MENU_H > window.innerHeight - 8 && rect.top > MENU_H;
    const style = { left, width: MENU_W, top: openUp ? rect.top - MENU_H - 4 : rect.bottom + 4 };

    return (
        <div className="dft-menu" role="menu" ref={menuRef} style={style} onKeyDown={onKeyDown}>
            {items.map((it, idx) => {
                const Icon = it.icon;
                const reason = it.unavailableReason || "";
                const reasonId = `${reasonBase}-${idx}`;
                return (
                    <button
                        key={it.label}
                        type="button"
                        role="menuitem"
                        className={`dft-menu-item ${it.danger ? "dft-menu-item-danger" : ""} ${reason ? "dft-menu-item-off" : ""}`}
                        aria-disabled={reason ? "true" : undefined}
                        aria-describedby={reason ? reasonId : undefined}
                        onClick={() => {
                            if (reason) return;     // not available; the reason is shown below the label
                            // Focus the row's menu button first so the dialog
                            // this opens hands focus back to it on close.
                            trigger.focus();
                            onClose();
                            it.onSelect();
                        }}
                    >
                        <Icon aria-hidden="true" />
                        <span className="dft-menu-text">
                            <span>{it.label}</span>
                            {reason && <span className="dft-menu-reason" id={reasonId}>{reason}</span>}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

// ── Dialog shell: Esc, focus trap, focus restore ──────────────────────────
// Dialogs can stack (a preview on top of Add). Only the top one of the stack
// reacts to Esc and keeps Tab inside it. Esc and Tab are also ignored while
// a dnAlert confirm is on top, so the confirm keeps the keyboard.
const alertOpen = () => !!document.querySelector(".dn-modal-backdrop");
const dialogStack = [];

function useDialogKeys(ref, onRequestClose) {
    const closeRef = useRef(onRequestClose);
    closeRef.current = onRequestClose;
    useEffect(() => {
        const token = {};
        dialogStack.push(token);
        const previous = document.activeElement;
        const node = ref.current;
        const first = node && node.querySelector("[data-autofocus]");
        (first || node)?.focus();
        const onKey = (e) => {
            if (dialogStack[dialogStack.length - 1] !== token) return;
            if (alertOpen() || !ref.current) return;
            if (e.key === "Escape") { e.preventDefault(); closeRef.current(); return; }
            if (e.key !== "Tab") return;
            const items = Array.from(ref.current.querySelectorAll(
                'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )).filter(el => el.offsetParent !== null);
            if (!items.length) return;
            const firstEl = items[0];
            const lastEl = items[items.length - 1];
            if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
            else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
        };
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("keydown", onKey);
            const i = dialogStack.indexOf(token);
            if (i >= 0) dialogStack.splice(i, 1);
            if (previous && typeof previous.focus === "function" && document.contains(previous)) previous.focus();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

function Dialog({ title, eyebrow, onRequestClose, children, footer, wide }) {
    const ref = useRef(null);
    const titleId = useRef(`dft-dialog-${Math.random().toString(36).slice(2)}`).current;
    useDialogKeys(ref, onRequestClose);
    return (
        <div className="dft-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onRequestClose(); }}>
            <div
                className={`dft-dialog ${wide ? "dft-dialog-wide" : ""}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                ref={ref}
                tabIndex={-1}
            >
                <div className="dft-dialog-header">
                    <div>
                        {eyebrow && <div className="dft-eyebrow">{eyebrow}</div>}
                        <h3 id={titleId}>{title}</h3>
                    </div>
                    <button type="button" className="dft-close" aria-label="Close" onClick={onRequestClose}>
                        <FaTimes aria-hidden="true" />
                    </button>
                </div>
                <div className="dft-dialog-body">{children}</div>
                {footer && <div className="dft-dialog-footer">{footer}</div>}
            </div>
        </div>
    );
}

// ── Preview dialog (View, and forms in Add) ───────────────────────────────
// Shows the real form in a frame from a dForms preview link. A new link is
// asked for every time the dialog opens or Reload is pressed; links are never
// cached. The frame is on another site so its content cannot be read: the
// expiry timer starts when the link arrives and the notice shows one minute
// before expires_in_minutes, so an expired page is never shown first.
const PREVIEW_DEFAULT_MINUTES = 30;

// Asks dForms for a fresh preview link (never cached) and tracks the frame's
// loading state and the expiry timer. Used by PreviewDialog and RefreshDialog.
function usePreviewLink(request) {
    const [link, setLink] = useState(null);             // { url, minutes }
    const [fetching, setFetching] = useState(true);
    const [frameLoaded, setFrameLoaded] = useState(false);
    const [error, setError] = useState("");
    const [expired, setExpired] = useState(false);
    const timerRef = useRef(null);
    const seq = useRef(0);
    const requestKey = JSON.stringify(request);

    const fetchLink = useCallback(async () => {
        const mine = ++seq.current;
        clearTimeout(timerRef.current);
        setFetching(true);
        setError("");
        setExpired(false);
        setFrameLoaded(false);
        setLink(null);
        const r = await callApi("/preview-link", jsonOptions("POST", JSON.parse(requestKey)));
        if (mine !== seq.current) return;               // closed, or a newer request
        setFetching(false);
        const url = r.ok && r.data && typeof r.data.url === "string" ? r.data.url : "";
        if (!url || !/^https?:\/\//i.test(url)) {
            const msg = r.ok ? "dForms did not return a usable preview link." : r.error;
            setError(msg);
            dnAlert.error("Could not open the preview", msg);
            return;
        }
        const minutes = Number(r.data.expires_in_minutes) > 0 ? Number(r.data.expires_in_minutes) : PREVIEW_DEFAULT_MINUTES;
        timerRef.current = setTimeout(() => setExpired(true), Math.max(0, minutes - 1) * 60000);
        setLink({ url, minutes });
    }, [requestKey]);

    useEffect(() => {
        fetchLink();
        return () => { seq.current += 1; clearTimeout(timerRef.current); };
    }, [fetchLink]);

    return { link, fetching, frameLoaded, setFrameLoaded, error, expired, fetchLink };
}

// The frame area: loading state, the iframe, the expiry notice, or the error
// dForms gave (for example a form that is no longer offered).
function PreviewFrame({ preview, name }) {
    const { link, frameLoaded, setFrameLoaded, error, expired, fetchLink } = preview;
    let content;
    if (error) {
        content = (
            <div className="dft-state dft-state-error" role="alert">
                <FaExclamationTriangle className="dft-state-icon" aria-hidden="true" />
                <h3>Could not open the preview</h3>
                <p>{error}</p>
                <button type="button" className="dft-btn dft-btn-secondary" onClick={fetchLink}>
                    <FaRedo aria-hidden="true" /> Retry
                </button>
            </div>
        );
    } else if (expired) {
        content = (
            <div className="dft-state dft-state-warn" role="status">
                <FaClock className="dft-state-icon" aria-hidden="true" />
                <h3>This preview link has expired</h3>
                <p>Preview links last {link ? link.minutes : PREVIEW_DEFAULT_MINUTES} minutes. Get a new link to keep looking.</p>
                <button type="button" className="dft-btn dft-btn-primary" onClick={fetchLink}>
                    <FaRedo aria-hidden="true" /> Reload preview
                </button>
            </div>
        );
    } else if (link) {
        content = (
            <>
                {!frameLoaded && (
                    <div className="dft-preview-loading" role="status">
                        <span className="dft-spinner" aria-hidden="true" /> Loading preview...
                    </div>
                )}
                <iframe
                    key={link.url}
                    className="dft-preview-iframe"
                    src={link.url}
                    title={`Preview of ${name}`}
                    onLoad={() => setFrameLoaded(true)}
                />
            </>
        );
    } else {
        content = (
            <div className="dft-preview-loading" role="status">
                <span className="dft-spinner" aria-hidden="true" /> Getting a preview link...
            </div>
        );
    }
    return <div className="dft-preview-frame">{content}</div>;
}

// "Open in new tab" (while the link is valid) and "Reload preview".
function PreviewLinkActions({ preview }) {
    const { link, expired, fetching, fetchLink } = preview;
    return (
        <>
            {link && !expired && (
                <a className="dft-btn dft-btn-secondary dft-btn-sm" href={link.url} target="_blank" rel="noopener noreferrer">
                    <FaExternalLinkAlt aria-hidden="true" /> Open in new tab
                </a>
            )}
            <button type="button" className="dft-btn dft-btn-secondary dft-btn-sm" onClick={fetchLink} disabled={fetching}>
                <FaRedo aria-hidden="true" /> Reload preview
            </button>
        </>
    );
}

function PreviewDialog({ request, name, badge, details, onClose, onUse }) {
    const ref = useRef(null);
    const titleId = useRef(`dft-preview-${Math.random().toString(36).slice(2)}`).current;
    const preview = usePreviewLink(request);

    useDialogKeys(ref, onClose);

    return (
        <div className="dft-overlay dft-overlay-preview" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="dft-preview" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref} tabIndex={-1}>
                <div className="dft-preview-header">
                    <div className="dft-preview-heading">
                        <div className="dft-eyebrow">{onUse ? "Form preview" : "Template preview"}</div>
                        <h3 id={titleId}>
                            {name}
                            {badge && (
                                <span className={`dft-badge dft-badge-${badge.kind}`} title={badge.hint}>
                                    {badge.text}<span className="dft-sr-only">: {badge.hint}</span>
                                </span>
                            )}
                        </h3>
                    </div>
                    <div className="dft-preview-actions">
                        <PreviewLinkActions preview={preview} />
                        {onUse && (
                            <button type="button" className="dft-btn dft-btn-primary dft-btn-sm" onClick={onUse}>
                                <FaCheck aria-hidden="true" /> Use this form
                            </button>
                        )}
                        <button type="button" className="dft-close" aria-label="Close preview" onClick={onClose} data-autofocus>
                            <FaTimes aria-hidden="true" />
                        </button>
                    </div>
                </div>
                {details && details.length > 0 && (
                    <details className="dft-preview-details" open>
                        <summary>Details</summary>
                        <dl>
                            {details.map(([label, value]) => (
                                <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
                            ))}
                        </dl>
                    </details>
                )}
                <PreviewFrame preview={preview} name={name} />
            </div>
        </div>
    );
}

// ── Source form search (Add) ──────────────────────────────────────────────
function FormSearch({ selected, onSelect, onPreview }) {
    const [q, setQ] = useState("");
    const [state, setState] = useState({ loading: true, error: "", results: [] });
    const [attempt, setAttempt] = useState(0);
    const seq = useRef(0);
    const inputId = useRef(`dft-form-search-${Math.random().toString(36).slice(2)}`).current;

    useEffect(() => {
        const mine = ++seq.current;
        setState(s => ({ ...s, loading: true, error: "" }));
        const timer = setTimeout(async () => {
            const r = await callApi(`/forms/search?q=${encodeURIComponent(q.trim())}`);
            if (mine !== seq.current) return;       // a newer search is on its way
            if (!r.ok) {
                setState({ loading: false, error: r.error, results: [] });
                return;
            }
            setState({ loading: false, error: "", results: Array.isArray(r.data) ? r.data : [] });
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [q, attempt]);

    const results = state.results;

    return (
        <div className="dft-form-search">
            <label htmlFor={inputId} className="dft-label">Search forms</label>
            <div className="dft-search">
                <FaSearch className="dft-search-icon" aria-hidden="true" />
                <input
                    id={inputId}
                    type="search"
                    className="dft-input"
                    placeholder="Form title"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    data-autofocus
                />
            </div>
            <p className="dft-note">{SEARCH_NOTE}</p>

            <div className="dft-results" aria-live="polite" aria-busy={state.loading ? "true" : "false"}>
                {state.loading && <div className="dft-results-msg">Searching...</div>}
                {!state.loading && state.error && (
                    <div className="dft-results-msg dft-results-error" role="alert">
                        {state.error}
                        <button type="button" className="dft-btn dft-btn-secondary dft-btn-sm" onClick={() => setAttempt(a => a + 1)}>
                            <FaRedo aria-hidden="true" /> Retry
                        </button>
                    </div>
                )}
                {!state.loading && !state.error && results.length === 0 && (
                    <div className="dft-results-msg">No forms found.</div>
                )}
                {!state.loading && !state.error && results.length > 0 && (
                    <fieldset className="dft-results-list">
                        <legend className="dft-sr-only">Choose a source form</legend>
                        {results.map(f => {
                            const checked = selected && selected.form_id === f.form_id;
                            return (
                                <div key={f.form_id} className={`dft-result-row ${checked ? "dft-result-on" : ""}`}>
                                <label className="dft-result">
                                    <input
                                        type="radio"
                                        name={`${inputId}-pick`}
                                        checked={!!checked}
                                        onChange={() => onSelect(f)}
                                    />
                                    <span className="dft-result-body">
                                        <span className="dft-result-title">
                                            {f.title || "Untitled form"}
                                            {checked && <FaCheck className="dft-result-check" aria-hidden="true" />}
                                        </span>
                                        <span className="dft-result-meta">
                                            {f.is_own ? "Your form" : `Owner: ${f.owner_name || f.owner_emp_id || "Unknown"}`}
                                            {" | "}{countsLabel(f)}
                                            {f.updated_at ? ` | Updated ${formatDate(f.updated_at)}` : ""}
                                        </span>
                                        {!f.offered && f.is_own && (
                                            <span className="dft-result-warn">Not offered as a template yet</span>
                                        )}
                                    </span>
                                </label>
                                {onPreview && (
                                    <button
                                        type="button"
                                        className="dft-btn dft-btn-secondary dft-btn-sm dft-result-preview"
                                        aria-label={`Preview ${f.title || "Untitled form"}`}
                                        onClick={() => onPreview(f)}
                                    >
                                        <FaEye aria-hidden="true" /> Preview
                                    </button>
                                )}
                                </div>
                            );
                        })}
                    </fieldset>
                )}
            </div>
        </div>
    );
}

// ── Title / description / category fields ─────────────────────────────────
function DetailsFields({ values, onChange, categories, idPrefix }) {
    const set = (k) => (e) => onChange({ ...values, [k]: e.target.value });
    return (
        <>
            <div className="dft-field">
                <label htmlFor={`${idPrefix}-title`} className="dft-label dft-label-req">Title</label>
                <input id={`${idPrefix}-title`} type="text" className="dft-input" maxLength={TITLE_MAX} required
                    value={values.title} onChange={set("title")} data-autofocus />
                <div className="dft-hint">{values.title.trim().length}/{TITLE_MAX}. Must be unique among templates.</div>
            </div>
            <div className="dft-field">
                <label htmlFor={`${idPrefix}-description`} className="dft-label">Description</label>
                <textarea id={`${idPrefix}-description`} className="dft-input" rows={3} maxLength={DESCRIPTION_MAX}
                    value={values.description} onChange={set("description")} />
                <div className="dft-hint">{values.description.length}/{DESCRIPTION_MAX}</div>
            </div>
            <div className="dft-field">
                <label htmlFor={`${idPrefix}-category`} className="dft-label">Category</label>
                <input id={`${idPrefix}-category`} type="text" className="dft-input" maxLength={CATEGORY_MAX}
                    list={`${idPrefix}-categories`} value={values.category} onChange={set("category")} />
                <datalist id={`${idPrefix}-categories`}>
                    {categories.map(c => <option key={c} value={c} />)}
                </datalist>
                <div className="dft-hint">Free text, up to {CATEGORY_MAX} characters. Existing categories are suggested.</div>
            </div>
        </>
    );
}

// ── Add template (2 steps) ────────────────────────────────────────────────
function AddDialog({ categories, onClose, onAdded, onNotConfigured }) {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState(null);
    const [values, setValues] = useState({ title: "", description: "", category: "" });
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [previewForm, setPreviewForm] = useState(null);   // preview dialog on top of this one
    // Guards against adding by accident. "Add template" appears where "Next"
    // was, so the second click of a double-click on Next (or a held Enter)
    // would submit step 2 straight away. A ref also stops two overlapping
    // requests, which state alone cannot.
    const submittingRef = useRef(false);
    const step2ShownAt = useRef(0);
    const ACCIDENTAL_SUBMIT_MS = 700;

    // Move focus to the first field of the step that was just shown.
    useEffect(() => {
        if (step === 2) step2ShownAt.current = Date.now();
        const el = document.querySelector(".dft-dialog [data-autofocus]");
        if (el) el.focus();
    }, [step]);

    const tooSoonAfterStep2 = () => Date.now() - step2ShownAt.current < ACCIDENTAL_SUBMIT_MS;

    const goNext = (picked = form) => {
        if (!picked) return;
        setValues(v => ({ ...v, title: v.title || String(picked.title || "").slice(0, TITLE_MAX) }));
        setError("");
        setStep(2);
    };

    // "Use this form" in the preview: select it and go straight to step 2.
    const pickFromPreview = () => {
        const f = previewForm;
        setPreviewForm(null);
        setForm(f);
        goNext(f);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (saving || submittingRef.current || tooSoonAfterStep2()) return;
        const err = validateDetails(values);
        if (err) { setError(err); return; }
        submittingRef.current = true;
        setSaving(true);
        setError("");
        const r = await callApi("/templates", jsonOptions("POST", {
            source_form_id: form.form_id,
            title: values.title.trim(),
            description: values.description.trim(),
            category: values.category.trim(),
        }));
        submittingRef.current = false;
        setSaving(false);
        if (r.notConfigured) { onNotConfigured(); return; }
        if (!r.ok) {
            setError(r.error);
            dnAlert.error("Could not add the template", r.error);
            return;
        }
        dnAlert.success("Template added", ADDED_MESSAGE);
        onAdded();
    };

    const requestClose = () => { if (!saving) onClose(); };

    return (
        <>
        <Dialog
            title="Add template"
            eyebrow={`Step ${step} of 2: ${step === 1 ? "Choose a form" : "Details"}`}
            onRequestClose={requestClose}
            wide
            footer={step === 1 ? (
                <>
                    <button type="button" className="dft-btn dft-btn-secondary" onClick={requestClose}>Cancel</button>
                    <button type="button" className="dft-btn dft-btn-primary" onClick={() => goNext()} disabled={!form}>Next</button>
                </>
            ) : (
                <>
                    <button type="button" className="dft-btn dft-btn-secondary" onClick={() => setStep(1)} disabled={saving}>Back</button>
                    <button
                        type="submit"
                        form="dft-add-form"
                        className="dft-btn dft-btn-primary"
                        disabled={saving}
                        onClick={(e) => {
                            // Second click of a double-click, or a click that
                            // landed right as step 2 appeared: do not submit.
                            if (e.detail > 1 || tooSoonAfterStep2()) e.preventDefault();
                        }}
                    >
                        {saving ? "Copying the form..." : "Add template"}
                    </button>
                </>
            )}
        >
            {step === 1 ? (
                <FormSearch selected={form} onSelect={setForm} onPreview={setPreviewForm} />
            ) : (
                <form
                    id="dft-add-form"
                    onSubmit={submit}
                    onKeyDown={(e) => { if (e.key === "Enter" && e.repeat) e.preventDefault(); }}
                    noValidate
                >
                    <div className="dft-picked">
                        <div className="dft-picked-label">Source form</div>
                        <div className="dft-picked-title">
                            {form.title || "Untitled form"}
                            <button
                                type="button"
                                className="dft-link-btn"
                                aria-label={`Preview ${form.title || "Untitled form"}`}
                                onClick={() => setPreviewForm(form)}
                                disabled={saving}
                            >
                                <FaEye aria-hidden="true" /> Preview
                            </button>
                        </div>
                        <div className="dft-muted dft-small">
                            {form.is_own ? "Your form" : `Owner: ${form.owner_name || "Unknown"}`} | {countsLabel(form)}
                        </div>
                    </div>
                    <DetailsFields values={values} onChange={setValues} categories={categories} idPrefix="dft-add" />
                    <p className="dft-note">The template starts switched off so you can check it first. Copying a large form can take up to a minute.</p>
                    {error && <div className="dft-form-error" role="alert">{error}</div>}
                </form>
            )}
        </Dialog>
        {previewForm && (
            <PreviewDialog
                request={{ form_id: previewForm.form_id }}
                name={previewForm.title || "Untitled form"}
                badge={previewForm.is_own
                    ? { text: "Your form", kind: "form", hint: "Not a template yet" }
                    : { text: "Offered", kind: "form", hint: "Offered as a template by its owner" }}
                details={formDetails(previewForm)}
                onClose={() => setPreviewForm(null)}
                onUse={step === 1 ? pickFromPreview : undefined}
            />
        )}
        </>
    );
}

// ── Edit details ──────────────────────────────────────────────────────────
function EditDialog({ template, categories, onClose, onSaved, onNotConfigured }) {
    const initial = useMemo(() => ({
        title: template.title || "",
        description: template.description || "",
        category: template.category || "",
    }), [template]);
    const [values, setValues] = useState(initial);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const changed = useMemo(() => {
        const out = {};
        for (const k of ["title", "description", "category"]) {
            if (values[k].trim() !== initial[k].trim()) out[k] = values[k].trim();
        }
        return out;
    }, [values, initial]);
    const dirty = Object.keys(changed).length > 0;

    const requestClose = async () => {
        if (saving) return;
        if (dirty) {
            const ok = await dnAlert.confirm({
                title: "Discard changes?",
                message: "You have unsaved changes to this template. Close without saving?",
                confirmLabel: "Discard",
                cancelLabel: "Keep editing",
                danger: true,
            });
            if (!ok) return;
        }
        onClose();
    };

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (!dirty) { onClose(); return; }
        const err = validateDetails(values);
        if (err) { setError(err); return; }
        setSaving(true);
        setError("");
        const r = await callApi(`/templates/${encodeURIComponent(template.id)}`, jsonOptions("PUT", changed));
        setSaving(false);
        if (r.notConfigured) { onNotConfigured(); return; }
        if (!r.ok) {
            setError(r.error);
            dnAlert.error("Could not save the details", r.error);
            return;
        }
        dnAlert.success("Details saved", values.title.trim());
        onSaved();
    };

    return (
        <Dialog
            title="Edit details"
            eyebrow={template.title}
            onRequestClose={requestClose}
            footer={(
                <>
                    <button type="button" className="dft-btn dft-btn-secondary" onClick={requestClose} disabled={saving}>Cancel</button>
                    <button type="submit" form="dft-edit-form" className="dft-btn dft-btn-primary" disabled={saving || !dirty}>
                        {saving ? "Saving..." : "Save"}
                    </button>
                </>
            )}
        >
            <form id="dft-edit-form" onSubmit={submit} noValidate>
                <DetailsFields values={values} onChange={setValues} categories={categories} idPrefix="dft-edit" />
                {error && <div className="dft-form-error" role="alert">{error}</div>}
            </form>
        </Dialog>
    );
}

// ── Refresh from source ───────────────────────────────────────────────────
// Shows the latest version of the template's current source form, then
// refreshes from it (empty body: dForms uses the current source). Only opened
// for templates that have a live source form (see refreshUnavailableReason).
function RefreshDialog({ template, onClose, onDone, onNotConfigured }) {
    const ref = useRef(null);
    const titleId = useRef(`dft-refresh-${Math.random().toString(36).slice(2)}`).current;
    const sourceTitle = template.source_form_title || "the source form";
    const preview = usePreviewLink({ form_id: template.source_form_id });
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);

    const requestClose = () => { if (!savingRef.current) onClose(); };
    useDialogKeys(ref, requestClose);

    // A refused preview (for example the owner no longer offers the form)
    // means the refresh would be refused too.
    const canRefresh = !saving && !preview.fetching && !preview.error;

    const submit = async () => {
        if (!canRefresh || savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setError("");
        const r = await callApi(`/templates/${encodeURIComponent(template.id)}/refresh`, jsonOptions("POST", {}));
        savingRef.current = false;
        setSaving(false);
        if (r.notConfigured) { onNotConfigured(); return; }
        if (!r.ok) {
            setError(r.error);
            dnAlert.error("Could not refresh the template", r.error);
            return;
        }
        dnAlert.success(`Template refreshed from ${sourceTitle}`, template.title);
        onDone(r.data);
    };

    return (
        <div className="dft-overlay dft-overlay-preview" onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
            <div className="dft-preview" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={`${titleId}-text`} ref={ref} tabIndex={-1}>
                <div className="dft-preview-header">
                    <div className="dft-preview-heading">
                        <div className="dft-eyebrow">Refresh from source</div>
                        <h3 id={titleId}>{template.title}</h3>
                    </div>
                    <div className="dft-preview-actions">
                        <PreviewLinkActions preview={preview} />
                        <button type="button" className="dft-close" aria-label="Close" onClick={requestClose} disabled={saving} data-autofocus>
                            <FaTimes aria-hidden="true" />
                        </button>
                    </div>
                </div>
                <p className="dft-refresh-text" id={`${titleId}-text`}>
                    Latest version of <strong>{sourceTitle}</strong>. Refreshing replaces the questions and design of <strong>{template.title}</strong> with this.
                    {" "}Its name, description, category, On/Off state, order and thumbnail stay the same. Forms already made from it are not affected.
                </p>
                <PreviewFrame preview={preview} name={sourceTitle} />
                <div className="dft-preview-footer">
                    {error && <div className="dft-form-error dft-footer-error" role="alert">{error}</div>}
                    <button type="button" className="dft-btn dft-btn-secondary" onClick={requestClose} disabled={saving}>Cancel</button>
                    <button type="button" className="dft-btn dft-btn-primary" onClick={submit} disabled={!canRefresh}>
                        <FaSyncAlt aria-hidden="true" /> {saving ? "Refreshing..." : "Refresh template"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Thumbnail ─────────────────────────────────────────────────────────────
function ThumbnailDialog({ template, currentUrl, onClose, onDone, onRemove, onNotConfigured }) {
    const [file, setFile] = useState(null);
    const [removing, setRemoving] = useState(false);
    const [preview, setPreview] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

    const onPick = (e) => {
        const f = e.target.files && e.target.files[0];
        setError("");
        if (!f) { setFile(null); setPreview(""); return; }
        const ext = (f.name.match(/\.[^.]+$/) || [""])[0].toLowerCase();
        if (!THUMBNAIL_EXTS.includes(ext) || !THUMBNAIL_TYPES.includes(f.type)) {
            setError("Choose a PNG, JPG or WebP image.");
            e.target.value = "";
            setFile(null); setPreview("");
            return;
        }
        if (f.size > THUMBNAIL_MAX) {
            setError("The image must be 2 MB or smaller.");
            e.target.value = "";
            setFile(null); setPreview("");
            return;
        }
        setFile(f);
        setPreview(URL.createObjectURL(f));
    };

    const submit = async () => {
        if (!file || saving) return;
        setSaving(true);
        setError("");
        const fd = new FormData();
        fd.append("thumbnail", file, file.name);
        const r = await callApi(`/templates/${encodeURIComponent(template.id)}/thumbnail`, { method: "POST", body: fd });
        setSaving(false);
        if (r.notConfigured) { onNotConfigured(); return; }
        if (!r.ok) {
            setError(r.error);
            dnAlert.error("Could not upload the thumbnail", r.error);
            return;
        }
        dnAlert.success("Thumbnail updated", template.title);
        onDone();
    };

    // Asks first (in the page's removeThumbnail); closes this dialog on success.
    const remove = async () => {
        if (saving || removing) return;
        setRemoving(true);
        try { await onRemove(); } finally { setRemoving(false); }
    };

    const busy = saving || removing;

    return (
        <Dialog
            title="Change thumbnail"
            eyebrow={template.title}
            onRequestClose={() => { if (!busy) onClose(); }}
            footer={(
                <>
                    <button type="button" className="dft-btn dft-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
                    <button type="button" className="dft-btn dft-btn-primary" onClick={submit} disabled={!file || busy}>
                        {saving ? "Uploading..." : "Upload"}
                    </button>
                </>
            )}
        >
            <div className="dft-thumb-compare">
                <figure>
                    <figcaption>Current</figcaption>
                    {currentUrl
                        ? <img className="dft-thumb-large" src={currentUrl} alt={`Current thumbnail of ${template.title}`} />
                        : <div className="dft-thumb-large dft-thumb-empty"><FaWpforms aria-hidden="true" /><span>No thumbnail</span></div>}
                    {template.thumbnail_path && (
                        <button type="button" className="dft-btn dft-btn-danger dft-btn-sm dft-thumb-remove" onClick={remove} disabled={busy}>
                            <FaTrashAlt aria-hidden="true" /> {removing ? "Removing..." : "Remove thumbnail"}
                        </button>
                    )}
                </figure>
                <figure>
                    <figcaption>New</figcaption>
                    {preview
                        ? <img className="dft-thumb-large" src={preview} alt="Preview of the chosen file" />
                        : <div className="dft-thumb-large dft-thumb-empty"><FaImage aria-hidden="true" /><span>Choose an image</span></div>}
                </figure>
            </div>
            <div className="dft-field">
                <label htmlFor="dft-thumb-file" className="dft-label">Image file</label>
                <input
                    id="dft-thumb-file"
                    type="file"
                    className="dft-file"
                    accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                    onChange={onPick}
                    disabled={busy}
                    data-autofocus
                />
                <div className="dft-hint">PNG, JPG or WebP, at most 2 MB. A thumbnail is optional.</div>
            </div>
            {error && <div className="dft-form-error" role="alert">{error}</div>}
        </Dialog>
    );
}
